import { formatCurrency } from '@util/index';
import { OrderDocument } from '@models/order';
import React from 'react';
import Chip from '@ui/Chip';
import { useAuth } from 'context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getEarningSummary } from '@util/getEarningsSummary';
import Link from 'next/link';

const EarningSummary = ({ order }: { order: OrderDocument }) => {
  const { userDoc } = useAuth();
  const { data: e } = useQuery({
    queryKey: ['earnings-summary', order, userDoc],
    queryFn: () => getEarningSummary(order, userDoc),
  });
  if (!e) return null;
  return (
    <div className="flex w-full flex-col items-start gap-[1.6rem] overflow-clip border-[1px] border-solid  border-[#D9D9D9] bg-white sm:rounded-[1.5rem]">
      <Header />
      <div className="flex w-full flex-col gap-[1.6rem] p-[2rem]">
        <div className="flex w-full justify-between">
          <span>Subtotal</span>
          <span>{formatCurrency(e.subtotal)}</span>
        </div>
        <div className="flex w-full justify-between">
          <span>Shipping</span>
          <span>{formatCurrency(e.shipping)}</span>
        </div>
        {!!e.tax && (
          <div className="flex w-full justify-between">
            <span>Tax</span>
            <span>{formatCurrency(e.tax)}</span>
          </div>
        )}
        {!!order.ra_fee && userDoc?.roles?.admin && (
          <div className="flex w-full justify-between">
            <span>Return Assurance</span>
            <span className="">{formatCurrency(order.ra_fee)}</span>
          </div>
        )}
        {!!order.affirm_fee && userDoc?.roles?.admin && (
          <div className="flex w-full justify-between">
            <span>Affirm Processing Fee</span>
            <span className="">{formatCurrency(order.affirm_fee)}</span>
          </div>
        )}

        {!!e.refunded && (
          <div className="flex w-full justify-between">
            <span>Refunded</span>
            <span className="text-brand-secondary">
              -{formatCurrency(e.refunded)}
            </span>
          </div>
        )}
        {!!order.coupon?.off && !!userDoc?.roles?.admin && (
          <div className="flex w-full justify-between">
            <div>
              <span>Discount</span>
              <span className="mx-2">{!!e.seller_coupon && '(Seller)'}</span>
              <Link
                target="_blank"
                href={`https://dashboard.stripe.com/coupons/${order.coupon.id}`}
                className="mx-2 text-brand-secondary"
              >
                ({order.coupon.code})
              </Link>
            </div>
            <span className="text-brand-secondary">
              -{formatCurrency(order.coupon.off)}
            </span>
          </div>
        )}
        {order.coupon?.seller_id === userDoc?.uid && (
          <div className="flex w-full justify-between">
            <span>Coupon</span>
            <span className="text-brand-secondary">
              -{formatCurrency(e.seller_coupon)}
            </span>
          </div>
        )}

        <div className="flex w-full justify-between">
          <span>Total</span>
          <span>
            {formatCurrency(
              e.total -
                (order.coupon?.seller_id === userDoc?.uid ? e.seller_coupon : 0)
            )}
          </span>
        </div>
        <div className="h-[1px] w-full border-b-[1px] border-dashed" />
        {!!e.shippingPaidByBuyer && (
          <div className="flex w-full justify-between">
            <div className="flex">
              <span className="mr-5">Shipping</span>
              <Chip text="Paid By Buyer" color="gray" />
            </div>
            <span className="text-brand-secondary">
              -{formatCurrency(e.shippingPaidByBuyer)}
            </span>
          </div>
        )}
        {!!e.insurance_total && (
          <div className="flex w-full justify-between">
            <span>Insurance Cost</span>
            <span className="text-brand-secondary">
              -{formatCurrency(e.insurance_total)}
            </span>
          </div>
        )}
        {!!(e.tax - e.remittedTax) && (
          <div className="flex w-full justify-between">
            <span>Tax</span>
            <span className="text-brand-secondary">
              -{formatCurrency(e.tax - e.remittedTax)}
            </span>
          </div>
        )}

        {!!order.affirm_fee && userDoc?.roles?.admin && (
          <div className="flex w-full justify-between">
            <span>Affirm Processing Fee</span>
            <span className="text-brand-secondary">
              -{formatCurrency(order.affirm_fee)}
            </span>
          </div>
        )}
        {!!order.ra_fee && userDoc?.roles?.admin && (
          <div className="flex w-full justify-between">
            <span>Return Assurance</span>
            <span className="text-brand-secondary">
              -{formatCurrency(order.ra_fee)}
            </span>
          </div>
        )}
        <div className="flex w-full justify-between">
          <span>Processing Fee</span>
          <span className="text-brand-secondary">
            -{formatCurrency(e.processing)}
          </span>
        </div>
        <div className="flex w-full justify-between">
          <span>MX Locker Fee</span>
          <span className="text-brand-secondary">
            -{formatCurrency(e.commission)}
          </span>
        </div>

        <div className="h-[1px] w-full border-b-[1px] border-dashed" />
        <div className="flex w-full flex-col gap-[0.4rem]">
          <div className="flex w-full justify-between">
            <span className="text-[1.8rem] font-semibold">Net Earnings</span>
            <span className="text-[2.4rem] font-semibold">
              {formatCurrency(Math.max(0, e.earnings))}
              {/* math.max request from andrew for the small window of partial refunds that go negative :) */}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => (
  <div className="flex w-full justify-between border-b-[1px] border-solid border-[#D9D9D9] bg-[#f0f2f2] p-[2rem]">
    <span className="text-[2.2rem] font-semibold">Earning Summary</span>
  </div>
);

export default EarningSummary;
